<template>
	<NuxtLink :to="profile.profileUrl" class="text-recommendation-profile">
		<img
			v-if="profile.avatarUrl"
			:src="profile.avatarUrl"
			:alt="profile.displayName"
			class="text-recommendation-profile__avatar"
		/>
		<div class="text-recommendation-profile__content">
			<span class="text-recommendation-profile__content__name">{{ profile.displayName }}</span>
			<div class="text-recommendation-profile__content__details">
				<span
					v-if="profile.profileType === 'JW_EDITOR'"
					class="text-recommendation-profile__content__details__role"
				>
					<img
						class="text-recommendation-profile__content__details__role__icon"
						height="12"
						src="/appassets/img/jw-icon.png"
						alt="JustWatch Logo"
					/>
					<span>
						{{ $t('WEBAPP_EDITOR') }}
					</span>
				</span>
				<span
					v-else
					class="text-recommendation-profile__content__details__role"
					:class="{ 'text-recommendation-profile__content__details__role--special': isSpecial }"
					>{{ displayRole }}</span
				>
				<span class="text-recommendation-profile__content__details__dash">-</span>
				<span class="text-recommendation-profile__content__details__count">{{ recommendationCountText }}</span>
			</div>
		</div>
	</NuxtLink>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { ProfileType } from '@/@types/graphql-types';
import type { ProfileInfoFragment } from '@/features/user-profile/graphql/fragments/ProfileInfo.fragment';

const { t } = useI18n();

const props = defineProps<{
	profile: ProfileInfoFragment;
	customProfileType?: string | null | undefined;
}>();

const specialProfileTypes = [
	ProfileType.Actor,
	ProfileType.Director,
	ProfileType.Influencer,
	ProfileType.Producer,
	ProfileType.Vip,
	ProfileType.Writer,
];

const profileTypeMapping: Record<ProfileType, string> = {
	ACTOR: t('WEBAPP_ACTOR'),
	DIRECTOR: t('WEBAPP_DIRECTOR'),
	INFLUENCER: t('WEBAPP_INFLUENCER'),
	JW_EDITOR: t('WEBAPP_JUSTWATCH_EDITOR'),
	JW_EMPLOYEE: t('WEBAPP_JUSTWATCH_EMPLOYEE'),
	PRODUCER: t('WEBAPP_PRODUCER'),
	PRO_USER: 'Pro',
	USER: '',
	WRITER: t('WEBAPP_WRITER'),
	VIP: t('WEBAPP_VIP'),
};

const displayRole = computed(() =>
	props.profile.profileType ? profileTypeMapping[props.profile.profileType] : t('WEBAPP_JUSTWATCH_EDITOR')
);

const isSpecial = computed(() => {
	if (props.customProfileType) return true;

	return props.profile.profileType != null && specialProfileTypes.includes(props.profile.profileType);
});

const recommendationCountText = computed(() =>
	props.profile.textRecommendationsCount === 1
		? t('WEBAPP_1_RECOMMENDATION')
		: t('WEBAPP_NR_OF_RECOMMENDATIONS', { count: props.profile.textRecommendationsCount })
);
</script>

<style lang="scss" scoped>
.text-recommendation-profile {
	display: flex;
	align-items: center;
	gap: 8px;
	z-index: 1;

	&__avatar {
		border-radius: 100%;
		width: 40px;
		min-width: 40px;
		height: 40px;
		min-height: 40px;
	}

	&__content {
		display: flex;
		flex-direction: column;
		gap: 8px;
		flex: 1;

		&__name {
			color: #fff;
			font-size: 14px;
			font-weight: 500;
			line-height: 16px;
			letter-spacing: 0.24px;
		}

		&__details {
			display: flex;
			align-items: center;
			gap: 4px;

			&__role {
				color: #d2d3d5;
				font-size: 14px;
				font-weight: 400;
				line-height: 16px;
				letter-spacing: 0.21px;

				display: flex;
				align-items: center;

				&__icon {
					margin-inline-end: 4px;
				}

				&--special {
					color: #fbc500;
				}
			}

			&__dash {
				color: #909398;
			}

			&__count {
				color: #909398;
				font-size: 14px;
				font-weight: 400;
				line-height: 16px;
				letter-spacing: 0.21px;
			}
		}
	}
}
</style>
